import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams, useLocation, Outlet } from 'react-router-dom';
import { media } from '../../styles/breakpoints';
import OrgPublicationBar from 'components/common/OrgPublicationBar';
import OrganizationSidebar, { SidebarItem } from './OrganizationSidebar';
import useDocumentTitle from '../../components/auth/useDocumentTitle';
import { switchAccount } from '../../utils/orgSwitch';

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;

  ${media.sidenav} {
    flex-direction: column;
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 20px 30px;
  min-width: 0;

  ${media.sidenav} {
    padding: 16px;
  }
`;

export const SIDEBAR_ITEMS: SidebarItem[] = [
  { label: 'Content portals', href: '/organizations/:id/portals', permissionKey: 'portals' },
  { label: 'Integrations', href: '/organizations/:id/integrations', permissionKey: 'integrations' },
  { label: 'Tags', href: '/organizations/:id/tag_groups', permissionKey: 'tag_groups' },
  { label: 'Users', href: '/organizations/:id/users', permissionKey: 'users' },
  { label: 'Single sign-on', href: '/organizations/:id/saml', permissionKey: 'saml' },
];

interface OrganizationData {
  name: string;
  accounts: { id: number; name: string }[];
  publications: { id: number; name: string }[];
  sidebar_permissions?: Record<string, boolean>;
}

// Map org subpages to their publication equivalents
const ORG_TO_PUB_SUBPAGE: Record<string, string> = {
  portals: 'portals',
  integrations: 'integrations',
  tag_groups: 'tag_groups',
  users: 'users/teams',
  saml: 'settings',
};

export default function OrganizationLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const [orgData, setOrgData] = useState<OrganizationData | null>(null);

  useDocumentTitle(orgData?.name ? `${orgData.name} Settings` : 'Organization Settings');

  useEffect(() => {
    if (!orgId) return;

    fetch(`/organizations/${orgId}.json`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(res => res.json())
      .then(data => {
        setOrgData({
          name: data.name,
          accounts: data.accounts ?? [],
          publications: data.publications ?? [],
          sidebar_permissions: data.sidebar_permissions,
        });
      })
      .catch(() => {
        // silently fail — bar will show without names
      });
  }, [orgId]);

  const handlePublicationChange = useCallback(
    (pubId: number) => {
      // Extract the subpage from the current org route and map to publication route
      const match = location.pathname.match(/\/organizations\/\d+\/(.+)/);
      const orgSubpage = match?.[1]?.split('/')?.[0] ?? '';
      const pubSubpage = ORG_TO_PUB_SUBPAGE[orgSubpage] ?? orgSubpage;
      window.location.href = `/publications/${pubId}/${pubSubpage}`;
    },
    [location.pathname],
  );

  return (
    <LayoutWrapper data-testid="organization-layout">
      <OrgPublicationBar
        accounts={orgData?.accounts ?? []}
        publications={orgData?.publications?.map(p => ({ id: p.id, name: p.name })) ?? []}
        currentAccountName={orgData?.name}
        currentPublicationName="ALL PUBLICATIONS"
        onPublicationChange={handlePublicationChange}
        onAccountChange={switchAccount}
        allPublicationsSelected
      />
      <ContentArea>
        <OrganizationSidebar
          items={SIDEBAR_ITEMS}
          orgId={orgId ?? ''}
          sidebarPermissions={orgData?.sidebar_permissions}
        />
        <MainContent>
          <Outlet />
        </MainContent>
      </ContentArea>
    </LayoutWrapper>
  );
}
